import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { io as ioClient } from "socket.io-client";
import api, { API_ORIGIN, assetUrl } from "../api";
import { filterFiles, isAttachmentFile, MAX_ATTACHMENTS, uploadErrorMessage } from "../uploadUtils";
import UserLayout from "../components/Userlayout";
import SafeImage from "../components/SafeImage";
import {
  Search, ListFilter, CheckCheck, Paperclip, Send, Calendar, Clock, FileText, Download, X, ChevronLeft,
} from "lucide-react";
import { TabBar, TabPanel, buildRows } from "../components/QuotePanels";


const full = assetUrl;
const money = (n) => `$${(Number(n) || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const initials = (name) => (name?.[0] || "?").toUpperCase();

const fmtListTime = (d) => {
  const date = new Date(d);
  const now = new Date();
  if (date.toDateString() === now.toDateString())
    return date.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  const days = Math.round((now - date) / 86400000);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString("en-AU", { day: "2-digit", month: "short" });
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const responseTime = (createdAt, quotedAt) => {
  if (!createdAt || !quotedAt) return "—";
  const mins = Math.round((new Date(quotedAt) - new Date(createdAt)) / 60000);
  if (mins < 0) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const fmtClock12 = (d) => (d ? new Date(d).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit", hour12: true }).toUpperCase() : "");
const fmtBubble = (d) => new Date(d).toLocaleString("en-AU", { day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });

const statusInfo = (status) => {
  if (status === "Approved") return { label: "Approved", cls: "bg-green-50 text-green-700", dot: "bg-green-500" };
  if (status === "Closed") return { label: "Declined", cls: "bg-red-50 text-red-600", dot: "bg-red-500" };
  return { label: "Pending", cls: "bg-[#fff1dc] text-[#d9680a]", dot: "bg-[#f59a1f]" };
};

const TABS = ["All", "Unread", "Open", "Closed"];

export default function UserComments() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "null");

  const [conversations, setConversations] = useState([]);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("All");
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [detail, setDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);

  const [panelTab, setPanelTab] = useState("overview");

  const fileRef = useRef(null);
  const threadEndRef = useRef(null);
  const socketRef = useRef(null);
  const joinedRoomRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/my/conversations");
      setConversations(Array.isArray(data) ? data : []);
    } catch {
      /* leave list as-is */
    }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // polling fallback
  useEffect(() => {
    const t = setInterval(loadConversations, 20000);
    return () => clearInterval(t);
  }, [loadConversations]);

  // socket: this user's own room for conversation-list updates
  useEffect(() => {
    if (!user?.id) return;
    const socket = ioClient(API_ORIGIN, { transports: ["websocket", "polling"] });
    socketRef.current = socket;
    socket.emit("join", `user:${user.id}`);

    socket.on("conversation:update", (payload) => {
      if (payload?.bulk) {
        setConversations((prev) => prev.map((c) => ({ ...c, unreadCount: 0 })));
        return;
      }
      setConversations((prev) => {
        const idx = prev.findIndex((c) => String(c.requestDocId) === String(payload.requestId));
        if (idx === -1) { loadConversations(); return prev; }
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          lastText: payload.lastText ?? next[idx].lastText,
          lastAt: payload.lastAt ?? next[idx].lastAt,
          lastAuthorRole: payload.lastAuthorRole ?? next[idx].lastAuthorRole,
          unreadCount: payload.unreadCount ?? next[idx].unreadCount,
        };
        next.sort((a, b) => new Date(b.lastAt) - new Date(a.lastAt));
        return next;
      });
    });

    return () => socket.disconnect();
  }, [user?.id, loadConversations]);

  // socket: join/leave the selected thread's room
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !selectedId) return;
    if (joinedRoomRef.current) socket.emit("leave", joinedRoomRef.current);
    const room = `request:${selectedId}`;
    socket.emit("join", room);
    joinedRoomRef.current = room;

    const onNew = (comment) => {
      if (String(comment.requestId) !== String(selectedId)) return;
      setMessages((prev) => (prev.some((m) => m._id === comment._id) ? prev : [...prev, comment]));
    };
    socket.on("comment:new", onNew);
    return () => socket.off("comment:new", onNew);
  }, [selectedId]);

  useEffect(() => { threadEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const openConversation = async (c) => {
    setSelectedId(c.requestDocId);
    setDetail(null);
    setMessages([]);
    setPanelTab("overview");

    try {
      const [r, m] = await Promise.all([
        api.get(`/requests/${c.requestDocId}`),
        api.get(`/requests/${c.requestDocId}/comments`),
      ]);
      setDetail(r.data);
      setMessages(m.data);
    } catch {
      /* ignore */
    }

    if (c.unreadCount > 0) {
      setConversations((prev) =>
        prev.map((x) => (x.requestDocId === c.requestDocId ? { ...x, unreadCount: 0 } : x))
      );
      api.patch(`/my/conversations/${c.requestDocId}/read`).catch(() => {});
    }
  };

  const addFiles = (list) => {
    const { accepted, problems } = filterFiles(list, { room: MAX_ATTACHMENTS - files.length, accept: isAttachmentFile });
    if (problems.length) alert(problems.join("\n"));
    if (accepted.length) setFiles((prev) => [...prev, ...accepted]);
  };

  // like the design, open the first conversation automatically on wide screens
  useEffect(() => {
    if (selectedId || conversations.length === 0) return;
    if (typeof window !== "undefined" && window.innerWidth >= 1024) openConversation(conversations[0]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations]);

  const send = async () => {
    if (sending || (!text.trim() && files.length === 0) || !selectedId) return;
    setSending(true);
    const fd = new FormData();
    fd.append("text", text);
    files.forEach((f) => fd.append("attachments", f));
    try {
      const { data } = await api.post(`/requests/${selectedId}/comments`, fd);
      // show it straight away; the socket event is deduped by _id (and may never arrive on some hosts)
      if (data?._id) setMessages((prev) => (prev.some((m) => m._id === data._id) ? prev : [...prev, data]));
      setText("");
      setFiles([]);
    } catch (err) {
      alert(uploadErrorMessage(err, "Could not send."));
    } finally {
      setSending(false);
    }
  };

  const markAllRead = async () => {
    setConversations((prev) => prev.map((c) => ({ ...c, unreadCount: 0 })));
    try { await api.patch("/my/conversations/read-all"); } catch { /* list already updated optimistically */ }
  };

  const visible = useMemo(() => {
    let list = conversations;
    if (tab === "Unread") list = list.filter((c) => c.unreadCount > 0);
    else if (tab === "Open") list = list.filter((c) => !["Approved", "Closed"].includes(c.status));
    else if (tab === "Closed") list = list.filter((c) => c.status === "Closed");

    const q = query.trim().toLowerCase();
    if (q) list = list.filter((c) => [c.quoteId, c.requestId, c.partRequired, c.lastText].filter(Boolean).some((v) => v.toLowerCase().includes(q)));
    return list;
  }, [conversations, tab, query]);

  const part = detail?.quoteParts?.[0] || {};
  const heroImg = part.image || detail?.partPhotos?.[0] || detail?.vehicleImage;
  const quoteAttachments = detail?.quoteAttachments || [];
  const s = detail ? statusInfo(detail.status) : null;
  const pick = (...vals) => vals.find((v) => v !== undefined && v !== null && String(v).trim() !== "");
  const itemRows = detail ? buildRows([
    ["Make", pick(part.make, detail.make, detail.vehicleMake)],
    ["Model", pick(part.model, detail.model, detail.vehicleModel)],
    ["Year", pick(part.year, detail.year, detail.vehicleYear)],
    ["Engine", pick(part.engine, detail.engine, detail.vehicleEngine)],
    ["Vehicle", part.vehicle],
    ["Condition", part.condition],
    ["Part Number", pick(part.partNumber, detail.partNumber)],
    ["Stock", part.stock],
    ["Location", part.location],
    ["KM", part.km],
    ["Supplier", "All Good Parts", true],
    ["Warranty", detail.quoteWarranty, true],
  ]) : [];
  const unreadTotal = conversations.reduce((n, c) => n + (c.unreadCount || 0), 0);

  return (
    <UserLayout>
      <main className="grid min-h-[calc(100vh-92px)] bg-[#f7f7f9] lg:grid-cols-[minmax(340px,38%)_1fr]">
        {/* ---------- left: conversation list ---------- */}
        <aside className={`bg-white px-6 py-8 md:px-10 ${selectedId ? "hidden lg:block" : ""}`}>
          <h1 className="text-[44px] font-extrabold leading-none text-[#0e1a30]">Comments</h1>
          <p className="mt-4 text-[15px] text-[#5d6472]">Search by Quote ID, Item and Part Number and stay updated with all conversations.</p>

          <div className="mt-7 flex items-center gap-3">
            <div className="flex h-[52px] flex-1 items-center gap-4 rounded-[10px] bg-[#f7f7f9] px-5">
              <Search size={18} className="text-[#777]" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by Quote ID, Item or Part Number..."
                className="min-w-0 flex-1 bg-transparent text-[14px] outline-none placeholder:text-[#9aa0aa]" />
            </div>
            <div className="relative">
              <button type="button" onClick={() => setShowFilterMenu((v) => !v)} aria-label="Filter conversations" title={`Filter: ${tab}`}
                className="grid h-[52px] w-[52px] place-items-center rounded-[10px] bg-[#f7f7f9] text-[#0e315c] hover:bg-slate-100">
                <ListFilter size={19} />
              </button>
              {showFilterMenu && (
                <div className="absolute right-0 z-20 mt-2 w-44 rounded-xl border border-gray-100 bg-white py-2 shadow-lg">
                  {TABS.map((t) => (
                    <button key={t} onClick={() => { setTab(t); setShowFilterMenu(false); }}
                      className={`w-full px-4 py-2 text-left text-sm ${tab === t ? "bg-slate-50 font-bold text-slate-900" : "text-slate-600 hover:bg-slate-50"}`}>{t}</button>
                  ))}
                </div>
              )}
            </div>
            <button type="button" onClick={markAllRead} aria-label="Mark all as read" title={unreadTotal ? `Mark ${unreadTotal} as read` : "Mark all as read"}
              className="grid h-[52px] w-[52px] place-items-center rounded-[10px] bg-[#f7f7f9] text-[#12a15b] hover:bg-slate-100">
              <CheckCheck size={19} />
            </button>
          </div>

          <div className="mt-6 max-h-[calc(100vh-330px)] space-y-3 overflow-y-auto pr-1">
            {visible.length === 0 && <p className="py-10 text-center text-sm text-slate-400">No conversations here.</p>}
            {visible.map((c) => {
              const active = selectedId === c.requestDocId;
              const label = c.partRequired || c.quoteId || c.requestId;
              return (
                <button key={c.requestDocId} onClick={() => openConversation(c)}
                  className={`flex w-full items-center gap-4 rounded-[12px] px-5 py-4 text-left transition ${active ? "bg-[#eef3ff] ring-1 ring-[#cfdcff]" : "bg-[#f7f7f9] hover:bg-[#f0f1f5]"}`}>
                  <span className="grid h-[42px] w-[42px] shrink-0 place-items-center rounded-full bg-[#0e315c] text-[15px] font-bold text-white">{initials(label)}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px] font-semibold text-[#172238]">{label}</span>
                    <span className="mt-1 block truncate text-[12px] text-[#9aa0aa]">{c.lastText || (c.lastAt ? "Attachment" : "No messages yet")}</span>
                  </span>
                  <span className="flex shrink-0 flex-col items-end gap-2">
                    <span className="text-[11px] text-[#9aa0aa]">{c.lastAt ? fmtListTime(c.lastAt) : ""}</span>
                    {c.unreadCount > 0 ? (
                      <span className="grid h-5 min-w-[20px] place-items-center rounded-full bg-[#dff5e8] px-1.5 text-[11px] font-bold text-[#12a15b]">{c.unreadCount}</span>
                    ) : c.lastAt ? (
                      <CheckCheck size={15} className="text-[#12a15b]" />
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        {/* ---------- right: quote + thread ---------- */}
        <section className={`min-w-0 bg-white px-6 py-8 lg:border-l lg:border-[#eef0f4] lg:px-8 ${selectedId ? "" : "hidden lg:block"}`}>
          {!selectedId ? (
            <div className="grid h-full min-h-[300px] place-items-center text-sm text-slate-400">Select a conversation to view messages.</div>
          ) : !detail ? (
            <div className="grid h-full min-h-[300px] place-items-center text-sm text-slate-400">Loading…</div>
          ) : (
            <>
              <button type="button" onClick={() => { setSelectedId(null); setDetail(null); }} className="mb-4 flex items-center gap-1 text-sm font-semibold text-[#0b7be7] lg:hidden">
                <ChevronLeft size={16} /> All conversations
              </button>

              {/* summary */}
              <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
                <div className="grid h-[140px] w-[137px] shrink-0 place-items-center overflow-hidden rounded-[14px] bg-[#f4f4fa]">
                  <SafeImage src={heroImg ? full(heroImg) : ""} alt={part.name || detail.partRequired || "Part"} className="h-full w-full object-contain p-2" />
                </div>
                <div className="min-w-0 flex-1">
                  {s && <span className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1 text-[11px] font-bold uppercase ${s.cls}`}>{s.label} <i className={`h-1.5 w-1.5 rounded-full ${s.dot}`} /></span>}
                  <h2 className="mt-2 text-[22px] font-extrabold leading-tight text-[#0e1a30]">{part.name || detail.partRequired || "Part Request"}</h2>
                  <p className="mt-1 text-[12px] text-[#858a94]">{part.partNumber || detail.partNumber || detail.quoteId || detail.requestId}</p>

                  <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="flex gap-2.5 rounded-[9px] bg-[#f5f8fd] px-4 py-3">
                      <Calendar size={16} className="mt-0.5 shrink-0 text-[#176eff]" />
                      <div>
                        <span className="block text-[9px] uppercase text-[#7a808b]">Quote Date</span>
                        <strong className="block text-[13px] text-[#172238]">{fmtDate(detail.quotedAt || detail.createdAt)}</strong>
                        <span className="block text-[9px] text-[#9aa0aa]">{fmtClock12(detail.quotedAt || detail.createdAt)}</span>
                      </div>
                    </div>
                    <div className="flex gap-2.5 rounded-[9px] bg-[#f5f8fd] px-4 py-3">
                      <Clock size={16} className="mt-0.5 shrink-0 text-[#176eff]" />
                      <div>
                        <span className="block text-[9px] uppercase text-[#7a808b]">Response Time</span>
                        <strong className="block text-[13px] text-[#172238]">{responseTime(detail.createdAt, detail.quotedAt)}</strong>
                      </div>
                    </div>
                    <div className="flex gap-2.5 rounded-[9px] bg-[#f5f8fd] px-4 py-3">
                      <Clock size={16} className="mt-0.5 shrink-0 text-[#176eff]" />
                      <div>
                        <span className="block text-[9px] uppercase text-[#7a808b]">Supplier</span>
                        <strong className="block text-[13px] text-[#172238]">All Good Parts</strong>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="shrink-0 sm:pt-16 sm:text-center">
                  <span className="block text-[12px] text-[#5d6472]">Quote Price</span>
                  <strong className="mt-1 block text-[20px] font-extrabold text-[#06953a]">
                    {detail.quotePricing?.total != null ? `${money(detail.quotePricing.total)} AUD` : "—"}
                  </strong>
                  <button type="button" onClick={() => navigate(`/quotes/${selectedId}`)} className="mt-1 text-[12px] font-semibold text-[#0b7be7] hover:underline">View full quote</button>
                </div>
              </div>

              {/* tabs + comments */}
              <div className="mt-8 grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                <div>
                  <TabBar tab={panelTab} onChange={setPanelTab} compact />
                  <div className="mt-5 min-h-[300px]">
                    <TabPanel
                      tab={panelTab}
                      rows={itemRows}
                      description={detail.quoteCustomerNote || "No description provided."}
                      attachments={quoteAttachments}
                      compact
                    />
                  </div>
                </div>

                <div className="rounded-[12px] bg-[#f7f7f9] p-6">
                  <h3 className="text-[16px] font-bold text-[#0e1a30]">Comments ({messages.length})</h3>

                  <div className="mt-4 flex items-center gap-3">
                    <span className="grid h-[44px] w-[44px] shrink-0 place-items-center rounded-full bg-[#e4e5ea] text-[17px] font-extrabold text-[#111]">{initials(user?.name)}</span>
                    <div className="flex h-[44px] min-w-0 flex-1 items-center gap-2 rounded-[10px] border border-[#e8eaf0] bg-white px-3.5">
                      <input value={text} onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && !sending && send()}
                        placeholder="Add a comment" className="min-w-0 flex-1 text-[13px] outline-none" />
                      <button type="button" onClick={() => fileRef.current?.click()} aria-label="Attach files" className="text-[#2f6df6]"><Paperclip size={17} /></button>
                      <input ref={fileRef} type="file" multiple hidden accept="image/*,application/pdf,audio/*,video/*"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                    </div>
                    <button type="button" onClick={send} disabled={sending} aria-label="Send comment" className="grid h-9 w-9 place-items-center text-[#2f6df6] disabled:opacity-50"><Send size={20} /></button>
                  </div>

                  {files.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {files.map((f, i) => (
                        <span key={i} className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs">
                          {f.name}
                          <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}><X size={12} /></button>
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="mt-6 max-h-[420px] overflow-y-auto pr-1">
                    {messages.map((m, idx) => {
                      const mine = m.authorRole === "user";
                      const last = idx === messages.length - 1;
                      return (
                        <div key={m._id} className="relative flex gap-3 pb-5">
                          {!last && <span className="absolute left-[21px] top-[46px] bottom-0 border-l border-dashed border-[#d7dae2]" />}
                          <span className={`z-[1] grid h-[44px] w-[44px] shrink-0 place-items-center rounded-full text-[17px] font-extrabold ${mine ? "bg-[#e4e5ea] text-[#111]" : "bg-[#e3ebff] text-[#2f6df6]"}`}>{initials(m.authorName)}</span>
                          <div className="min-w-0 flex-1 pt-0.5">
                            <div className="flex flex-wrap items-baseline gap-x-2.5">
                              <span className="text-[14px] font-bold text-[#0e1a30]">{mine ? "You" : m.authorName}</span>
                              {!mine && <span className="text-[11px] font-semibold text-[#2f6df6]">Supplier</span>}
                              <span className="text-[10px] text-[#7c828d]">{fmtBubble(m.createdAt)}</span>
                            </div>
                            {m.text && <p className="mt-1.5 whitespace-pre-line text-[13.5px] text-[#555b66]">{m.text}</p>}
                            {m.attachments?.length > 0 && (
                              <div className="mt-2 space-y-2">
                                {m.attachments.map((a, i) =>
                                  a.type === "image" ? (
                                    <a key={i} href={full(a.url)} target="_blank" rel="noreferrer"><img src={full(a.url)} alt={a.name} className="max-w-[200px] rounded-xl border" /></a>
                                  ) : a.type === "audio" ? (
                                    <audio key={i} src={full(a.url)} controls className="h-9" />
                                  ) : a.type === "video" ? (
                                    <video key={i} src={full(a.url)} controls className="max-w-[240px] rounded-xl" />
                                  ) : (
                                    <a key={i} href={full(a.url)} download target="_blank" rel="noreferrer"
                                      className="inline-flex items-center gap-2 rounded-lg border border-gray-100 bg-white p-2 text-sm hover:bg-slate-50">
                                      <FileText size={16} className="text-red-500" /> {a.name} <Download size={14} className="text-slate-400" />
                                    </a>
                                  )
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {messages.length === 0 && <p className="py-8 text-center text-sm text-slate-400">No comments yet.</p>}
                    <div ref={threadEndRef} />
                  </div>
                </div>
              </div>
            </>
          )}
        </section>
      </main>
    </UserLayout>
  );
}
